import type { CabinetTenantMembership, MyInvitation } from "@planerius/contracts/cabinet-auth"
import { systemRole } from "@planerius/contracts/cabinet-roles"

/**
 * Выбор салона (1-Э1.4) как данные: какая строка что говорит и можно ли в неё войти.
 *
 * Тексты — из листа «Неделя и телефон администратора — П1» (1-Э1.4). ГЛАВНОЕ ПРАВИЛО ЛИСТА:
 * строка, в которую нельзя войти, не прячется и не «падает в ошибку» при нажатии, а заранее
 * говорит почему. Приостановленный доступ и заблокированный за неоплату салон остаются
 * в списке — человек должен понимать, куда делся его салон, а не гадать.
 *
 * «Вас пригласила Ольга Ким» из макета сказано без рода: «Вас приглашает …». Род по имени
 * мы не угадываем — ошибка здесь задевает человека, а нейтральная форма не задевает никого.
 */

export type TenantRowKind = "enter" | "invitation" | "suspended_member" | "blocked_tenant" | "closed"

export interface TenantRow {
  readonly key: string
  readonly kind: TenantRowKind
  readonly name: string
  readonly sub: string
  readonly badge: { readonly text: string; readonly tone: "warn" | "gray" | "error" } | null
  readonly note: string | null
  readonly action: string | null
  /** Для входа — салон, для приглашения — идентификатор приглашения. */
  readonly tenantId: string
  readonly invitationId: string | null
}

function branchesPart(count: number, names: readonly string[] | null): string {
  if (names !== null) {
    if (names.length === 0) return "все филиалы"
    return names.length === 1 ? `филиал ${names[0] ?? ""}` : `филиалов: ${names.length}`
  }
  return count === 0 ? "все филиалы" : `филиалов: ${count}`
}

function roleSummary(code: string | null): string | null {
  if (code === null) return null
  const summary = systemRole(code)?.summary
  if (summary === undefined) return null
  return summary.charAt(0).toLowerCase() + summary.slice(1)
}

export function membershipRow(membership: CabinetTenantMembership): TenantRow {
  const base = {
    key: `m:${membership.tenantId}`,
    name: membership.tenantName,
    sub: `${membership.roleName} · ${branchesPart(membership.branchIds.length, null)}`,
    tenantId: membership.tenantId,
    invitationId: null,
  }
  if (membership.tenantStatus === "archived") {
    return { ...base, kind: "closed", badge: { text: "Салон закрыт", tone: "gray" }, note: null, action: null }
  }
  if (membership.memberStatus === "suspended") {
    return {
      ...base,
      kind: "suspended_member",
      badge: { text: "Доступ приостановлен", tone: "gray" },
      note: "Доступ приостановлен владельцем. Салон останется в списке — обратитесь к владельцу.",
      action: null,
    }
  }
  if (membership.tenantStatus === "suspended") {
    /*
     * СТРОКА ОТКРЫВАЕТСЯ, А НЕ ТОЛЬКО ПОМЕЧАЕТСЯ. Она была надписью без кнопки, и это
     * закрывало владельцу сети путь к собственным деньгам и данным: экран ограничения,
     * кнопка «Оплатить» и выгрузка базы живут ВНУТРИ кабинета, а войти было нельзя.
     * ТЗ (cabinet/01-основа.md) говорит прямо: «Салон заблокирован за неоплату. Виден,
     * помечен, открывается в Э1.12».
     *
     * Текст подписи зависит от того, кто читает: владельцу «откроется после оплаты
     * владельцем» — насмешка, ему надо внутрь и платить.
     */
    const forOwner = membership.roleCode === "owner"
    return {
      ...base,
      kind: "enter",
      badge: { text: "Заблокирован за неоплату", tone: "error" },
      note: forOwner
        ? "Записи и данные сохранены. Откройте салон, чтобы оплатить или выгрузить базу."
        : "Откроется после оплаты владельцем. Записи и данные сохранены — войти можно.",
      action: null,
    }
  }
  const trial = membership.tenantStatus === "trial" ? "Пробный период" : null
  const grace = membership.tenantStatus === "grace" ? "Оплата просрочена" : null
  const hint = trial ?? grace
  return {
    ...base,
    kind: "enter",
    badge: hint === null ? null : { text: hint, tone: grace === null ? "warn" : "error" },
    note: null,
    action: null,
  }
}

export function invitationRow(invitation: MyInvitation): TenantRow {
  const summary = roleSummary(invitation.roleCode)
  const who = invitation.invitedByName === null ? "Вас приглашают" : `Вас приглашает ${invitation.invitedByName}`
  const role = summary === null ? invitation.roleName.toLowerCase() : `${invitation.roleName.toLowerCase()} — ${summary}`
  return {
    key: `i:${invitation.invitationId}`,
    kind: "invitation",
    name: invitation.tenantName,
    sub: `${invitation.roleName} · ${branchesPart(0, invitation.branchNames)}`,
    badge: { text: "Приглашение", tone: "warn" },
    note: `${who}. Роль: ${role}`,
    action: "Принять приглашение",
    tenantId: invitation.tenantId,
    invitationId: invitation.invitationId,
  }
}

/**
 * Строки экрана: приглашения — сверху (их ждут, и макет выделяет их рамкой), дальше
 * салоны. Салон, в котором человек уже состоит, приглашением не дублируется: сервер
 * такие приглашения не отдаёт, но строка защищена и здесь.
 */
export function tenantRows(
  memberships: readonly CabinetTenantMembership[],
  invitations: readonly MyInvitation[],
): readonly TenantRow[] {
  const joined = new Set(memberships.filter((m) => m.memberStatus !== "invited").map((m) => m.tenantId))
  const invites = invitations.filter((invitation) => !joined.has(invitation.tenantId)).map(invitationRow)
  const salons = memberships.filter((m) => m.memberStatus !== "invited").map(membershipRow)
  return [...invites, ...salons]
}

/**
 * Заголовок над списком салонов, когда человек пришёл сюда после отказа.
 *
 * НАЗВАНИЕ САЛОНА ОБЯЗАТЕЛЬНО, И ОНО ТЕРЯЛОСЬ. Экран печатал заголовок отказа как есть
 * («Доступ к салону отозван»), а человеку с двумя салонами это не говорит ничего: он
 * не знает, из какого его убрали, и звонит в оба. Сервер название присылает — отдельным
 * полем `tenantName` в теле отказа (auth.errors.ts) и словами в пояснении.
 *
 * Возвращается ЗАГОЛОВОК, а пояснение экран берёт из detail: два источника одной мысли
 * разошлись бы на первой же правке текста на сервере.
 */
export function accessLostTitle(problem: {
  readonly code: string
  readonly title: string
  readonly raw: Readonly<Record<string, unknown>>
}): string {
  const name = typeof problem.raw["tenantName"] === "string" ? problem.raw["tenantName"] : null
  if (name === null) return problem.title
  if (problem.code === "tenant_access_revoked") return `Доступ к салону «${name}» отозван`
  if (problem.code === "tenant_suspended") return `Салон «${name}» приостановлен`
  return problem.title
}
